/**
 * Feedback Modal Component
 * 
 * A modal dialog for submitting feedback on chat messages.
 * Features thumbs up/down toggle buttons, comment box, and submit functionality.
 * 
 * Constraints:
 * - Comments limited to 10,000 characters (~50KB)
 */

import React, { useId, useState } from 'react';
import { FaRegThumbsUp, FaRegThumbsDown } from 'react-icons/fa';
import { useFormatters } from '../context/LocalizationContext';
import { useTranslation } from '../localization/useAppTranslation';
import { logger } from '../utils/logger';
import '../styles/AdminDashboard.css';

const moduleLogger = logger.getModule('FeedbackModal');

// Maximum length for feedback comments (must match server-side validation)
const MAX_COMMENT_LENGTH = 10000;

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comments: string) => Promise<void>;
  initialRating?: number; // 1 for thumbs up, 0 for thumbs down
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialRating = 1
}) => {
  const { t } = useTranslation('chat');
  const { formatNumber } = useFormatters();
  const titleId = useId();
  const [rating, setRating] = useState<number>(initialRating);
  const [comments, setComments] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Calculate character count and check if limit exceeded
  const commentLength = comments.length;
  const isLimitExceeded = commentLength > MAX_COMMENT_LENGTH;

  const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    // Allow typing up to the limit, but don't prevent exceeding (for paste operations)
    // The submit button will be disabled if limit is exceeded
    setComments(newValue);
  };

  const handleSubmit = async () => {
    if (isSubmitting || isLimitExceeded) return;
    
    // Final validation before submit
    if (commentLength > MAX_COMMENT_LENGTH) {
      moduleLogger.warn('Comment exceeds maximum length', { 
        length: commentLength, 
        max: MAX_COMMENT_LENGTH 
      });
      return;
    }

    setIsSubmitting(true);
  setSubmitError(null);
    try {
      await onSubmit(rating, comments.trim());
      onClose();
      // Reset form
      setComments('');
      setRating(1);
    } catch (error) {
      moduleLogger.error('Failed to submit feedback', error);
      setSubmitError(t('feedback.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    // Reset form
    setComments('');
    setRating(initialRating);
    setSubmitError(null);
  };

  // Reset rating when modal opens with new initial rating
  React.useEffect(() => {
    if (isOpen) {
      setRating(initialRating);
      setSubmitError(null);
    }
  }, [isOpen, initialRating]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div
        className="modal-content feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2 id={titleId}>{t('feedback.title')}</h2>
          <button 
            type="button"
            className="modal-close" 
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t('feedback.close')}
            title={t('feedback.close')}
          >
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Rating Section */}
          <div>
            <p>{t('feedback.ratingQuestion')}</p>
            <div>
              <button
                type="button"
                className={`btn btn-md ${rating === 1 ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setRating(1)}
                disabled={isSubmitting}
                aria-label={t('feedback.helpfulAriaLabel')}
                title={t('feedback.helpfulTitle')}
              >
                <span><FaRegThumbsUp aria-hidden="true" /></span>
                <span>{t('feedback.helpful')}</span>
              </button>
              <button
                type="button"
                className={`btn btn-md ${rating === 0 ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setRating(0)}
                disabled={isSubmitting}
                aria-label={t('feedback.notHelpfulAriaLabel')}
                title={t('feedback.notHelpfulTitle')}
              >
                <span><FaRegThumbsDown aria-hidden="true" /></span>
                <span>{t('feedback.notHelpful')}</span>
              </button>
            </div>
          </div>

          {/* Comments Section */}
          <div>
            <label htmlFor="feedback-comments">
              {t('feedback.commentsLabel')}
            </label>
            <div>
              <textarea
                id="feedback-comments"
                value={comments}
                onChange={handleCommentChange}
                placeholder={t('feedback.commentsPlaceholder')}
                disabled={isSubmitting}
                maxLength={MAX_COMMENT_LENGTH}
                title={isLimitExceeded ? t('feedback.commentTooLong', {
                  count: commentLength - MAX_COMMENT_LENGTH,
                  formattedCount: formatNumber(commentLength - MAX_COMMENT_LENGTH),
                  formattedMaximum: formatNumber(MAX_COMMENT_LENGTH),
                }) : ''}
              />
              <div>
                {t('feedback.characterCount', {
                  count: formatNumber(commentLength),
                  maximum: formatNumber(MAX_COMMENT_LENGTH),
                })}
              </div>
              {isLimitExceeded && (
                <div className="error-message" role="alert">
                  {t('feedback.commentTooLong', {
                    count: commentLength - MAX_COMMENT_LENGTH,
                    formattedCount: formatNumber(commentLength - MAX_COMMENT_LENGTH),
                    formattedMaximum: formatNumber(MAX_COMMENT_LENGTH),
                  })}
                </div>
              )}
              {submitError && <div className="error-message" role="alert">{submitError}</div>}
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button
            type="button"
            className="btn btn-md btn-ghost"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-md btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || isLimitExceeded}
            title={isLimitExceeded ? t('feedback.reduceToSubmit') : ''}
          >
            {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;